import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { ERROR } from "@/config/toast-message-config";
import { showError, showErrorWithApiError } from "@/shared/utils/toast-utils";
import { useUserLogin, useSendOTP } from "@/app/auth/services/auth";
import { canStartLoginChallenge, needsEmailOtpSend, resolveLoginChallenge, withEmailOtpSent } from "@/app/auth/utils/loginChallenge";
import orbiusLogo from "@/assets/orbius_logo.png";
import launchBetterImage from "@/assets/Launch_better.jpg";
import { ROUTES } from "@/config/routeConfig";
import { EyeOff, Eye } from "lucide-react";
import GenericAlertDialog from "@/shared/components/GenericAlertDialog";

const LoginPage = () => {
  const navigate = useNavigate();
  const login = useUserLogin();
  const sendOTP = useSendOTP();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordChangeDialogOpen, setIsPasswordChangeDialogOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onLogin(email, password);
  }

  const onLogin = async (email: string, password: string) => {
    try {
      const loginData = await login.mutateAsync({ email: email, password: password });
      if (loginData && loginData.requiresPasswordChange === true) {
        setIsPasswordChangeDialogOpen(true);
        return;
      }

      const challenge = resolveLoginChallenge(loginData);
      if (!loginData?.authId || !challenge) {
        showError(ERROR.LOGIN.LOGIN_FAILED);
        return;
      }

      let nextChallenge = challenge;
      if (needsEmailOtpSend(challenge)) {
        try {
          const sendResponse = await sendOTP.mutateAsync({ authId: loginData.authId });
          nextChallenge = resolveLoginChallenge(
            sendResponse ?? { authId: loginData.authId, challenge: sendResponse?.challenge ?? withEmailOtpSent(challenge) },
          ) ?? withEmailOtpSent(challenge);
          if (nextChallenge.emailOtp?.sent !== true) {
            nextChallenge = withEmailOtpSent(nextChallenge);
          }
        } catch (error) {
          showErrorWithApiError(error, ERROR.OTP.OTP_NOT_SENT);
          return;
        }
      }

      if (canStartLoginChallenge(nextChallenge)) {
        navigate(`${ROUTES.VERIFY}?email=${encodeURIComponent(email)}`, {
          state: { authId: loginData.authId, challenge: nextChallenge },
          replace: true,
        });
        return;
      }

      showError(ERROR.OTP.OTP_NOT_SENT);
    } catch (error) {
      showErrorWithApiError(error, ERROR.LOGIN.LOGIN_FAILED);
    }
  }

  return (
    <>
      <div className="relative min-h-screen overflow-hidden bg-muted">
        <img
          src={launchBetterImage}
          alt="Launch Better"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-right -scale-x-100"
        />

        <div className="relative z-10 flex min-h-screen w-full flex-col p-5 gap-5">
          <div className="flex w-full items-center justify-between">
            <img src={orbiusLogo} alt="Orbius Logo" className="h-16 w-auto drop-shadow-md" />

            <Button
              variant="outline"
              onClick={() => window.open('mailto:support@orbius.com', '_blank')}
            >
              Contact us
            </Button>
          </div>

          <div className="grid grid-cols-2 flex-1 items-center justify-center">
            <div className="w-full h-full">
            </div>
            <div className="flex items-center justify-center w-full h-full">
              <Card className="w-full max-w-md border shadow-sm">
                <CardHeader>
                  <CardTitle>
                    Log In
                  </CardTitle>
                  <CardDescription>
                    Enter your email address and password to log in.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="username" className="text-sm font-medium">
                        Email
                      </Label>
                      <Input
                        id="username"
                        type="text"
                        placeholder=""
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full"
                        autoComplete="email"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-sm font-medium">
                        Password
                      </Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder=""
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full"
                          autoComplete="current-password"
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Login Button */}
                    <Button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90"
                      disabled={login.isPending || sendOTP.isPending}
                    >
                      {login.isPending || sendOTP.isPending ? "Logging in..." : "Log In"}
                    </Button>
                  </form>

                  {/* Forgot Password Link */}
                  <div className="text-center mt-4">
                    <Link
                      to={ROUTES.FORGOT_PASSWORD}
                      className="text-sm text-primary hover:text-primary/80 underline"
                    >
                      Forgot your password?
                    </Link>
                  </div>

                  {/* Registration Section */}
                  <div className="mt-6 pt-6 border-t border-border">
                    <div className="text-center">
                      <p className="text-sm mb-4">
                        If you don't have an account, please contact your administrator or <a href="mailto:support@orbius.com" className="text-primary hover:text-primary/80 underline">support@orbius.com</a> to create an account.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Required Dialog */}
      <GenericAlertDialog
        open={isPasswordChangeDialogOpen}
        onOpenChange={setIsPasswordChangeDialogOpen}
        title="Password Change Required"
        description={ERROR.LOGIN.PASSWORD_CHANGE_REQUIRED}
        confirmLabel="Change Password"
        onConfirm={() => {
          navigate(ROUTES.FORGOT_PASSWORD, { replace: true });
          setIsPasswordChangeDialogOpen(false);
        }}
      />
    </>
  );
};

export default LoginPage;
